import classNames from 'classnames';
import classNamesBind from 'classnames/bind';
import { getDaysInMonth, parse } from 'date-fns';
import { ChangeEvent, useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { RepeatInput } from '~/gql/graphql';
import { getOrdinalSuffix } from '~/helpers/ordinalSuffix';
import { Input } from '../form/TextField';
import { Select } from '../form/downshift/Select';
import { Avatar } from './Avatar';
import styles from './JobsRepeat.module.css';
import { Switch } from './nucleus/Switch';

type JobsRepeatProps = {
  label: string;
  className?: string; // Used to shake the number of Repeats field
  onBlur?: () => void;
};

export type RepeatValue = {
  isRepeat: boolean;
  interval: string;
  numOfRepeats: string;
  frequency: string;
  repeatOnWeek: string[];
  repeatOnMonth: string;
  repeatOnYear: string;
};

export function getMonthByDay(value: string) {
  const arr = value.split(',');
  return [arr[0], arr[1].substring(0, 2).toLowerCase()].join('');
}
export function repeatToInput(value: RepeatValue): RepeatInput | null {
  if (!value.isRepeat) {
    return null;
  }

  const bymonthday =
    value.frequency === 'monthly' &&
    value.repeatOnMonth.startsWith('bymonthday:')
      ? value.repeatOnMonth.substring('bymonthday:'.length)
      : undefined;
  const byday =
    value.frequency === 'monthly' && value.repeatOnMonth.startsWith('byday:')
      ? [getMonthByDay(value.repeatOnMonth.substring('byday:'.length))]
      : value.frequency === 'weekly'
      ? value.repeatOnWeek.map((day) => day.substring(0, 2).toLowerCase())
      : undefined;
  return {
    freq: value.frequency,
    interval: value.interval,
    byday,
    bymonthday,
    count: parseInt(value.numOfRepeats),
  };
}

export const DAYS: string[] = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

const frequencyOptions = ['daily', 'weekly', 'monthly'];
export const getWeekNumber = (date: Date) => {
  const dateNumber = date.getDate();
  const daysInMonth = getDaysInMonth(date);
  const isLastWeekDayOfMonth = daysInMonth / (dateNumber + 7) < 1;
  return isLastWeekDayOfMonth ? 5 : Math.ceil(dateNumber / 7);
};

const cx = classNamesBind.bind(styles);

export function JobsRepeat({ onBlur, className }: JobsRepeatProps) {
  const { t } = useTranslation();
  const {
    control,
    register,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext();
  const { repeat, scheduledStartDate, scheduledEndDate } = watch();

  // Update the watch effect to handle clearing repeats when dates are removed
  useEffect(() => {
    if (!scheduledStartDate && !scheduledEndDate && repeat?.isRepeat) {
      setValue('repeat.isRepeat', false);
      setValue('repeat.numOfRepeats', '');
    }
  }, [scheduledStartDate, scheduledEndDate]);

  watch((data, { name: _, type }) => {
    const isOnChange = type === 'change';
    if (isOnChange && !data.scheduledStartDate && !data.scheduledEndDate) {
      setValue('repeat.isRepeat', false);
    }
  });

  // scheduledStartDate is a YYYY-MM-DD string (property-local)
  // Parse it directly without timezone conversion to avoid browser timezone issues
  const now = new Date();
  let formStartDate: Date;
  if (scheduledStartDate) {
    try {
      // Parse YYYY-MM-DD string directly - this creates a Date at midnight local time
      // but we only use it for getDate(), getDay(), getWeekNumber() which don't depend on timezone
      const parsed = parse(scheduledStartDate, 'yyyy-MM-dd', new Date());
      formStartDate = isNaN(parsed.getTime()) ? now : parsed;
    } catch {
      formStartDate = now;
    }
  } else {
    formStartDate = now;
  }
  const startDateWeekNumber = getWeekNumber(formStartDate);

  const monthOptions = [
    {
      value: `bymonthday:${formStartDate.getDate()}`,
      label: `Day ${formStartDate.getDate()} of the month`,
    },
    {
      value: `byday:${startDateWeekNumber},${DAYS[formStartDate.getDay()]}`,
      label: `${
        startDateWeekNumber > 4 ? 'Last' : getOrdinalSuffix(startDateWeekNumber)
      } ${DAYS[formStartDate.getDay()]} of the month`,
    },
  ];
  const numOfRepeatsError = errors.numOfRepeats?.message || '';

  return (
    <div className='flex flex-col'>
      <div data-usetiful='Repeats' className='mb-5 flex items-center gap-2'>
        <Controller
          name='repeat.isRepeat'
          control={control}
          render={({ field: { value, onChange } }) => {
            return (
              <Switch
                label='Repeats'
                active={scheduledStartDate || scheduledEndDate}
                checked={value}
                onChange={(checked) => {
                  onChange(checked); // ✅ Properly update form state
                }}
                tooltip='Select a Start or an End Date to activate Repeats'
              />
            );
          }}
        />
      </div>
      {repeat?.isRepeat && (
        <>
          <div
            data-usetiful='repeat-interval'
            className='flex flex-nowrap items-stretch gap-2'
          >
            <div className='basis-full 2xl:basis-1/3'>
              <Input
                {...register('repeat.interval')}
                label='Every'
                type='number'
              />
            </div>
            <Controller
              control={control}
              name='repeat.frequency'
              render={({ field }) => (
                <div className='min-w-[118px] basis-full 2xl:basis-1/3'>
                  <label className='ml-1 text-sm font-medium text-grey-50'></label>
                  <Select
                    hasSelectorIcon
                    inputProps={{ className: '' }}
                    label=''
                    options={frequencyOptions.map((value) => ({
                      value,
                      label: t(`repeatFrequencies.${value}`),
                    }))}
                    value={field.value ? [field.value] : []}
                    onChange={(selected) =>
                      field.onChange(selected.length ? selected[0] : '')
                    }
                  />
                </div>
              )}
            />
            <Controller
              control={control}
              name='repeat.numOfRepeats'
              render={({ field, fieldState: { error } }) => (
                <div className='flex min-w-[118px] basis-full 2xl:basis-1/3'>
                  <Input
                    className={classNames('w-full', className)}
                    value={field.value}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                      const { value } = event.target;
                      event.target.value = value.replace(/\D+/g, '');
                      field.onChange(event);
                    }}
                    onBlur={() => {
                      field.onBlur();
                      onBlur?.();
                    }}
                    label='No. of Repeats'
                    inputMode='numeric'
                    // @ts-ignore FIXME
                    error={error?.message || errors?.repeat?.message}
                    required={watch('repeat.isRepeat')}
                  />
                </div>
              )}
            />
          </div>
          {repeat?.frequency !== 'daily' ? (
            <div className='mb-8 flex flex-col gap-2 '>
              <span>Repeat on</span>
              {repeat?.frequency === 'weekly' ? (
                <Controller
                  control={control}
                  name='repeat.repeatOnWeek'
                  render={({ field }) => (
                    <div className='flex items-center justify-between gap-0 sm:justify-start sm:gap-1 lg:gap-2'>
                      {DAYS.map((day, index) => (
                        <div
                          className='rounded-full'
                          onClick={() => {
                            const values = [...field.value];
                            field.onChange(
                              values.includes(day)
                                ? values.filter((val) => val !== day)
                                : [...values, day]
                            );
                          }}
                          key={index}
                        >
                          <Avatar
                            className={classNames(
                              '!h-10 !w-10 cursor-pointer',
                              {
                                '!bg-brand text-white':
                                  repeat?.repeatOnWeek.includes(day),
                              }
                            )}
                            name={day}
                            size='medium'
                          />
                        </div>
                      ))}
                    </div>
                  )}
                />
              ) : null}
              {repeat?.frequency === 'monthly' ? (
                <Controller
                  control={control}
                  name='repeat.repeatOnMonth'
                  render={({ field }) => (
                    <Select
                      hasSelectorIcon
                      label=''
                      inputProps={{ className: '' }}
                      options={monthOptions}
                      value={field.value ? [field.value] : []}
                      onChange={(selected) =>
                        field.onChange(
                          selected.length ? selected[0] : monthOptions[0].value
                        )
                      }
                      sortItems={false}
                    />
                  )}
                />
              ) : null}
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
